import { Badge, Box, Button, Grid, Group, Stack, Table, Tabs, Text } from "@mantine/core"
import { ArrowLeftIcon, ArrowsClockwiseIcon, EyeIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { useSearchParams } from "react-router-dom"
import type { IDeliveryExceptionWithDn } from "../../apis/failed-delivery"
import * as FailedDeliveryApi from "../../apis/failed-delivery"
import { renderBusinessName } from "../../utils/render-business-name"
import { DeliveryRowAction } from "../delivery/action-controls"
import { DotPill, FrameFooter, TablePager, Tone } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { CustomTable, ITableRef } from "../table/customTable"
import { ListState } from "../ui"
import { ExceptionDetail } from "./ExceptionDetail"
import { ExceptionsTable } from "./ExceptionsTable"
import { useExceptionsByDnQuery, useFailedStatusCounts } from "./hooks"
import { ReassignDialog } from "./ReassignDialog"
import classes from "./failed.module.css"

// Spec §2.5 Tab 6 — Амжилтгүй sub-tabs by lifecycle stage:
// OPEN = awaiting attribution / resolution; ESCALATED = raised to a manager (day 2);
// RESOLVED = closed (attribution confirmed + reassigned / cancelled, or delivered).
type StatusFilter = "OPEN" | "ESCALATED" | "RESOLVED"

const STATUS_TABS: StatusFilter[] = ["OPEN", "ESCALATED", "RESOLVED"]

const STATUS_TONE: Record<string, Tone> = {
  OPEN: "amber",
  IN_PROGRESS: "blue",
  ESCALATED: "red",
  RESOLVED: "green",
  CLOSED: "grey",
}

// Spec §F-1 R-DEL-008: max 3 attempts.
const ATTEMPT_LIMIT = 3

function formatAge(raisedAt: string, t: TFunction): string {
  const minutes = Math.max(0, dayjs().diff(dayjs(raisedAt), "minute"))
  if (minutes < 60) return t("failed.ageMin", { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t("failed.ageHour", { count: hours })
  return t("failed.ageDay", { count: Math.floor(hours / 24) })
}

/** Амжилтгүй tab (canvas Ord_DeliveryTabsA, failed section). */
export function FailedDeliveries() {
  const { t } = useTranslation("delivery")
  const { stateOf } = useDeliveryActions()
  const [searchParams, setSearchParams] = useSearchParams()
  const dnId = searchParams.get("dn")
  const statusParam = searchParams.get("status") as StatusFilter | null
  const status = statusParam ?? "OPEN"
  const exParam = searchParams.get("ex")
  const [selectedExId, setSelectedExId] = useState<string | null>(exParam)
  const [reassignTarget, setReassignTarget] = useState<IDeliveryExceptionWithDn | null>(null)
  const tableRef = useRef<ITableRef>(null)

  // Detail mode (dn param set) — ExceptionsTable + ExceptionDetail panel.
  const { data: dnExceptions, isLoading: dnLoading, error: dnError } = useExceptionsByDnQuery(dnId)
  const { counts } = useFailedStatusCounts()
  const detailMode = !!dnId
  const filters = useMemo(() => ({ status }), [status])

  // Landing without a sub-tab while OPEN is empty but ESCALATED has rows: open ESCALATED.
  useEffect(() => {
    if (detailMode || statusParam || !counts) return
    if ((counts.OPEN ?? 0) === 0 && (counts.ESCALATED ?? 0) > 0) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set("status", "ESCALATED")
          return params
        },
        { replace: true },
      )
    }
  }, [detailMode, statusParam, counts, setSearchParams])

  const handleStatusChange = (next: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set("status", (next as StatusFilter) ?? "OPEN")
      return params
    })
  }

  const openDetail = (row: IDeliveryExceptionWithDn) => {
    if (!row.deliveryNoteId) return
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set("dn", row.deliveryNoteId)
      params.set("ex", row.id)
      return params
    })
  }

  const handleBackToList = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.delete("dn")
      params.delete("ex")
      return params
    })
    setSelectedExId(null)
  }

  // Keep selectedExId in sync with ?ex= and the loaded rows.
  const dnRows = useMemo(() => dnExceptions?.records ?? [], [dnExceptions])
  useEffect(() => {
    if (!detailMode) {
      if (selectedExId) setSelectedExId(null)
      return
    }
    if (dnRows.length === 0) return
    const urlMatch = exParam && dnRows.find((r) => r.id === exParam)
    const next = urlMatch ? urlMatch.id : dnRows[0]?.id ?? null
    if (next && next !== selectedExId) setSelectedExId(next)
  }, [detailMode, exParam, dnRows, selectedExId])

  if (detailMode) {
    const dnInfo = dnExceptions?.deliveryNote ?? null
    const selectedEx = selectedExId ? dnRows.find((r) => r.id === selectedExId) ?? null : null
    const hasMultiple = dnRows.length > 1
    return (
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap">
          <Button variant="default" leftSection={<ArrowLeftIcon size={16} />} onClick={handleBackToList}>
            {t("failed.backToList")}
          </Button>
          {dnInfo && (
            <Box ta="right">
              <Text ff="monospace" fw={650} fz="var(--nw-fz-mono)">
                {dnInfo.refCode ?? "-"}
              </Text>
              {dnInfo.receiverBusiness && (
                <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                  {renderBusinessName(dnInfo.receiverBusiness, "-")}
                </Text>
              )}
            </Box>
          )}
        </Group>
        {dnError && <ListState state="error" description={translateBackendError(dnError)} />}
        {hasMultiple ? (
          <Grid>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <ExceptionsTable rows={dnRows} selectedId={selectedExId} onSelect={setSelectedExId} loading={dnLoading} />
            </Grid.Col>
            {selectedEx && (
              <Grid.Col span={{ base: 12, md: 5 }}>
                <ExceptionDetail deliveryNoteId={dnId!} exception={selectedEx} />
              </Grid.Col>
            )}
          </Grid>
        ) : selectedEx ? (
          <ExceptionDetail deliveryNoteId={dnId!} exception={selectedEx} />
        ) : (
          !dnLoading && !dnError && <ListState state="empty" title={t("failed.noExceptionForDn")} />
        )}
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      <Tabs value={status} onChange={handleStatusChange}>
        <Tabs.List>
          {STATUS_TABS.map((value) => (
            <Tabs.Tab
              key={value}
              value={value}
              rightSection={
                counts ? (
                  <Badge size="md" radius="xl" ff="monospace" color={value === status ? "dark" : "gray"} variant={value === status ? "filled" : "light"}>
                    {counts[value] ?? 0}
                  </Badge>
                ) : null
              }
            >
              {t(`failed.tab.${value}`)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Box className={classes.frame}>
        <CustomTable
          minWidth={1200}
          ref={tableRef}
          name="failed-exceptions"
          colCount={8}
          filters={filters}
          loadData={async (filter) => {
            const offset = filter?.offset ?? { page: 1, limit: 20 }
            const f = (filter?.filter ?? {}) as { status?: string }
            return FailedDeliveryApi.list({
              status: f.status ?? null,
              limit: offset.limit,
              skip: (offset.page - 1) * offset.limit,
            })
          }}
          thead={
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={100}>{t("manage.col.action")}</Table.Th>
                <Table.Th>{t("failed.colOrder")}</Table.Th>
                <Table.Th>{t("failed.colReason")}</Table.Th>
                <Table.Th>{t("failed.colStatus")}</Table.Th>
                <Table.Th>{t("failed.colAttribution")}</Table.Th>
                <Table.Th>{t("failed.colAttempt")}</Table.Th>
                <Table.Th>{t("failed.colWaited")}</Table.Th>
                <Table.Th>{t("failed.colRaised")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
          }
          tbody={(rows: IDeliveryExceptionWithDn[]) => (
            <Table.Tbody>
              {rows.map((row) => {
                const photoCount = row.photoUrls?.length ?? 0
                const draft = row.attributionDraft && !row.attribution ? row.attributionDraft : null
                const reassignState = stateOf("reassign", row)
                return (
                  <Table.Tr key={row.id} className={classes.row} onClick={() => openDetail(row)}>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap="xs" wrap="nowrap">
                        <DeliveryRowAction label={t("exTable.detail")} icon={<EyeIcon />} onClick={() => openDetail(row)} />
                        {reassignState.applies && (
                          <DeliveryRowAction
                            label={t("exDetail.reassign")}
                            icon={<ArrowsClockwiseIcon />}
                            state={reassignState}
                            onClick={() => setReassignTarget(row)}
                          />
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fw={650} fz="var(--nw-fz-mono)">
                        {row.deliveryNote?.refCode ?? "-"}
                      </Text>
                      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                        {renderBusinessName(row.deliveryNote?.receiverBusiness, "-")}
                      </Text>
                      {photoCount > 0 && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {t("failed.photoCount", { count: photoCount })}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)">{t(`failed.exTypeOption.${row.exceptionType}`, { defaultValue: "-" })}</Text>
                    </Table.Td>
                    <Table.Td>
                      <DotPill tone={STATUS_TONE[row.status] ?? "grey"}>
                        {t(`failed.exStatus.${row.status}`, { defaultValue: "-" })}
                      </DotPill>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)">
                        {row.attribution
                          ? t(`failed.attribution.${row.attribution}`, { defaultValue: "-" })
                          : draft
                            ? t("manage.failed.driverDraft", { label: t(`failed.attribution.${draft}`, { defaultValue: "-" }) })
                            : "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)" fw={row.attemptNumber >= ATTEMPT_LIMIT ? 650 : 400}>
                        {row.attemptNumber}/{ATTEMPT_LIMIT}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)">{row.status === "RESOLVED" ? "-" : formatAge(row.raisedAt, t)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
                        {dayjs(row.raisedAt).format("YYYY-MM-DD HH:mm")}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          )}
          footer={(p) => (
            <FrameFooter>
              <TablePager page={p.page} pageSize={p.limit} total={p.total} onPageChange={p.setPage} onPageSizeChange={p.setLimit} />
            </FrameFooter>
          )}
        />
      </Box>

      {reassignTarget && (
        <ReassignDialog
          opened
          onClose={() => {
            setReassignTarget(null)
            tableRef.current?.reload()
          }}
          deliveryNoteId={reassignTarget.deliveryNoteId}
          previousExceptionId={reassignTarget.id}
        />
      )}
    </Stack>
  )
}
